<div class="w-full flex flex-col gap-y-2">
    <div class="flex flex-row items-center gap-x-4 w-full pt-2">
        <h2 class="text-sm font-semibold text-primary flex-nowrap min-w-fit">Bidding And Optimization</h2>
        <div class="border-b border-gray-200 dark:border-gray-800 w-full"></div>
        <button (click)="expanded.set(!expanded())" type="button" class="fab-button-secondary-small text-secondary">
            @if (expanded()) {
            <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none"
                stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" class="size-5">
                <path d="m18 15-6-6-6 6" />
            </svg>
            } @else {
            <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none"
                stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" class="size-5">
                <path d="m6 9 6 6 6-6" />
            </svg>
            }
        </button>
    </div>

    @if (expanded()) {
    <article class="briefing-section-card relative isolate flex flex-col gap-8 lg:flex-row">
        <div class="grid grid-cols-1 gap-x-6 gap-y-8 sm:grid-cols-6">
            <div class="sm:col-span-full">
                <label for="ad-group-name-input" class="block text-sm/6 font-medium text-primary">
                    Bidding And Optimization
                    <p class="text-xs text-secondary">Set your bidding strategy and optimization goal.</p>
                </label>
            </div>

            <div class="sm:col-span-full">
                <p id="ad-group-optimization-goal-heading"
                    class="block text-sm/6 font-medium text-gray-900 dark:text-white">
                    Optimization Goal</p>
                <p id="ad-group-optimization-goal-hint" class="text-xs text-secondary">The measurable results you'd like
                    to drive with your ads.</p>
                <div class="mt-2 grid grid-cols-1 gap-2 sm:grid-cols-3" role="radiogroup"
                    aria-labelledby="ad-group-optimization-goal-heading"
                    aria-describedby="ad-group-optimization-goal-hint">
                    @for (goal of allowedOptimizationGoals(); track goal) {
                    <button type="button" role="radio" [attr.aria-checked]="isOptimizationGoalSelected(goal)"
                        (click)="selectOptimizationGoal(goal)" [class]="optimizationGoalOptionClass(goal)">
                        {{ formatOptimizationGoalLabel(goal) }}
                    </button>
                    }
                </div>
            </div>

            <div class="sm:col-span-3">
                <p id="ad-group-frequency-cap-heading"
                    class="block text-sm/6 font-medium text-gray-900 dark:text-white">
                    Frequency cap</p>
                <p id="ad-group-frequency-cap-hint" class="text-xs text-secondary">Control how often people see your ad.
                    Target frequency is the average number of times a person will be shown your ad within a set period
                    of
                    time.</p>
                <div class="mt-2 flex flex-wrap items-center gap-x-2 gap-y-2 text-sm text-gray-900 dark:text-gray-100 w-full"
                    role="group" aria-labelledby="ad-group-frequency-cap-heading"
                    aria-describedby="ad-group-frequency-cap-hint">
                    <input id="ad-group-frequency-impressions" type="number" name="frequencyImpressions" min="1"
                        step="1" inputmode="numeric" [value]="frequencyImpressionsDisplay()"
                        (input)="onFrequencyImpressionsInput($event)"
                        class="w-16 min-w-0 rounded-md border-0 bg-white py-1.5 px-2 text-center text-sm text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 focus:ring-2 focus:ring-inset focus:ring-primary dark:bg-white/5 dark:text-white dark:ring-white/10 dark:focus:ring-primary" />
                    <span class="shrink-0">impressions</span>
                    <span class="shrink-0 text-gray-500 dark:text-gray-400" aria-hidden="true">/</span>
                    <input id="ad-group-frequency-schedule-days" type="number" name="frequencyScheduleDays" min="1"
                        step="1" inputmode="numeric" [value]="frequencyScheduleDaysDisplay()"
                        (input)="onFrequencyScheduleDaysInput($event)"
                        class="w-16 min-w-0 rounded-md border-0 bg-white py-1.5 px-2 text-center text-sm text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 focus:ring-2 focus:ring-inset focus:ring-primary dark:bg-white/5 dark:text-white dark:ring-white/10 dark:focus:ring-primary" />
                    <span class="shrink-0">day</span>
                </div>
            </div>


            <div class="sm:col-span-full grid grid-cols-2 gap-6">
                <div class="sm:col-span-1">
                    <p id="ad-group-bid-type-heading" class="block text-sm/6 font-medium text-gray-900 dark:text-white">
                        Bid strategy</p>
                    <p id="ad-group-bid-type-hint" class="text-xs text-secondary">Choose maximum delivery or set a
                        custom
                        bid cap when your billing event supports it (CPC, CPM, or CPV).</p>
                    <div class="mt-2 isolate flex w-full min-w-0 rounded-md shadow-xs dark:shadow-none"
                        role="radiogroup" aria-labelledby="ad-group-bid-type-heading"
                        aria-describedby="ad-group-bid-type-hint">
                        @for (opt of bidTypeOptions; track opt.value) {
                        <button type="button" role="radio" [attr.aria-checked]="isBidTypeSelected(opt.value)"
                            (click)="selectBidType(opt.value)" [class]="bidTypeOptionClass(opt.value, $index)">
                            {{ opt.label }}
                        </button>
                        }
                    </div>
                </div>

                @if (bidControlVisible()) {
                <div class="sm:col-span-1">
                    <label for="ad-group-bid-price"
                        class="block text-sm/6 font-medium text-gray-900 dark:text-white">Bid
                        Control
                        <p class="text-xs text-secondary">We suggest a bid of 2.43GBP to ensure your cost stays at or
                            below
                            this amount. For maximum delivery bidding, choose &quot;Maximum delivery&quot; above instead
                            of clearing this field.</p>
                    </label>
                    <div class="mt-2 flex flex-row gap-x-2 w-full items-center">
                        <input id="ad-group-bid-price" type="number" name="adGroupBidPrice" min="0" step="0.01"
                            inputmode="decimal" [value]="bidPriceDisplay()" (input)="onBidPriceInput($event)"
                            class="block w-full min-w-0 rounded-md bg-white px-3 py-1.5 text-base text-gray-900 outline-1 -outline-offset-1 outline-gray-300 placeholder:text-gray-400 focus:outline-2 focus:-outline-offset-2 focus:outline-primary sm:text-sm/6 dark:bg-white/5 dark:text-white dark:outline-white/10 dark:placeholder:text-gray-500 dark:focus:outline-primary" />
                        <span class="shrink-0 text-sm text-gray-500 dark:text-gray-400">GBP / Thousand
                            impressions</span>
                    </div>
                </div>
                }
            </div>
        </div>
    </article>
    }
</div>